import { VideoSDKMeeting } from '@videosdk.live/rtc-js-prebuilt'
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Roles } from 'utils/enums'

const resumeRemountDelayMs = 300
const resumeRemountArmDelayMs = 10000

const wordsToFind = {
  'End Call': 'Finalizar Llamada',
  'Turn on webcam': 'Prender cámara',
  'Turn off webcam': 'Apagar cámara',
  'Change webcam': 'Cambiar cámara',
  'Turn on mic': 'Prender micrófono',
  'Turn off mic': 'Apagar micrófono',
  'Change microphone': 'Cambiar micrófono',
  'More Options': 'Más Opciones',
  Participants: 'Participantes',
  'Present Screen': 'Presentar Pantalla',
  You: 'Yo',
  'Request Screenshare': 'Solicitar compartir pantalla',
  'Search Participants': 'Buscar Participantes',
  'Write your message': 'Escribir mensaje',
  MICROPHONE: 'MICRÓFONO',
  'Virtual Background (BETA)': 'Fondo Virtual',
  'Add custom background to meetings':
    'Añadir fondos customizados a las reuniones',
  'Add Live Streams': 'Añadir Live Stream',
  'Broadcast live streams to other platforms':
    'Transmitir transmisiones en vivo a otras plataformas',
  'You are presenting to everyone': 'Estas presentando',
}

const config = {
  embedBaseUrl: process.env.REACT_APP_DEVELOPMENT !== 'True'
    ? process.env.REACT_APP_VIDEO_CALL_DOMAIN
      || 'https://videollamada-service.vercel.app/rtc-js-prebuilt/0.3.23'
    : 'http://localhost:3002',
  containerId: 'videoSDKContainer',
  mode: 'CONFERENCE',
  maxResolution: 'hd',

  maintainVideoAspectRatio: true,
  maintainLandscapeVideoAspectRatio: true,
  micEnabled: true,
  webcamEnabled: true,
  chatEnabled: true,
  screenShareEnabled: true,

  pollEnabled: false,
  whiteboardEnabled: false,
  raiseHandEnabled: false,
  networkBarEnabled: false,

  participantCanToggleSelfWebcam: true,
  participantCanToggleSelfMic: true,
  participantTabPanelEnabled: true,
  joinWithoutUserInteraction: true,

  participantCanLeave: true,
  canCreatePoll: false,
  layout: {
    type: 'SIDEBAR',
    priority: 'SPEAKER',
  },
  livestream: {
    enabled: false,
  },
  hls: {
    enabled: false,
  },

  permissions: {
    removeParticipant: false,
    toggleParticipantMic: false,
    toggleParticipantWebcam: false,
    toggleParticipantMode: false,
    toggleParticipantScreenshare: false,
    toggleLivestream: false,
    toggleHls: false,
    canCreatePoll: false,
    pin: true,
    changeLayout: false,
  },
  waitingScreen: {
    text: 'Uniéndose a la reunión',
  },
  notificationSoundEnabled: true,
}
const VideoSDK = ({
  meetingId,
  participantName,
  roleObject,
  token,
  redirectOnLeave,
  participantCanLeave = true,
  remountOnPageResume = false,
}) => {
  const containerRef = useRef(null)
  const hiddenAtRef = useRef(null)
  const remountTimeoutRef = useRef(null)
  const resumeArmTimeoutRef = useRef(null)
  const resumeArmedRef = useRef(false)
  const meetingOptionsRef = useRef(null)
  const [resumeRevision, setResumeRevision] = useState(0)

  meetingOptionsRef.current = {
    participantCanLeave,
    participantName,
    redirectOnLeave,
    roleObject,
    token,
  }

  const armResumeRemount = useCallback(() => {
    if (!remountOnPageResume) return
    resumeArmedRef.current = false
    window.clearTimeout(resumeArmTimeoutRef.current)
    resumeArmTimeoutRef.current = window.setTimeout(() => {
      resumeArmedRef.current = true
    }, resumeRemountArmDelayMs)
  }, [remountOnPageResume])

  const scheduleResumeRemount = useCallback(() => {
    if (!remountOnPageResume || hiddenAtRef.current === null) return
    hiddenAtRef.current = null
    window.clearTimeout(remountTimeoutRef.current)
    remountTimeoutRef.current = window.setTimeout(() => {
      setResumeRevision((revision) => revision + 1)
    }, resumeRemountDelayMs)
  }, [remountOnPageResume])

  useEffect(() => {
    if (!remountOnPageResume) return undefined

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        window.clearTimeout(resumeArmTimeoutRef.current)
        if (!resumeArmedRef.current) {
          hiddenAtRef.current = null
          return
        }
        resumeArmedRef.current = false
        hiddenAtRef.current = Date.now()
        window.clearTimeout(remountTimeoutRef.current)
        return
      }
      scheduleResumeRemount()
      armResumeRemount()
    }
    const handlePageShow = (event) => {
      if (!event.persisted) return
      if (hiddenAtRef.current === null) hiddenAtRef.current = Date.now()
      scheduleResumeRemount()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pageshow', handlePageShow)
    armResumeRemount()
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pageshow', handlePageShow)
      window.clearTimeout(remountTimeoutRef.current)
      window.clearTimeout(resumeArmTimeoutRef.current)
    }
  }, [armResumeRemount, remountOnPageResume, scheduleResumeRemount])

  useEffect(() => {
    if (!meetingId) return
    const container = containerRef.current
    const {
      participantCanLeave: canLeave,
      participantName: name,
      redirectOnLeave: leaveRedirect,
      roleObject: role,
      token: meetingToken,
    } = meetingOptionsRef.current
    container?.replaceChildren()
    const meeting = new VideoSDKMeeting()
    const isTranslator = role?.role === Roles.Translator.role
    const redirectUrl = new URL(
      leaveRedirect ?? `${role.mainPath}/callFinished`,
      window.location.origin,
    )
    config.redirectOnLeave = redirectUrl.toString()
    meeting.init({
      ...config,
      token: meetingToken,
      permissions: {
        ...config.permissions,
        endMeeting: isTranslator,
        removeParticipant: isTranslator,
        participantCanLeave: canLeave,
      },
      participantCanLeave: canLeave,
      name,
      meetingId,
    })
    return () => container?.replaceChildren()
  }, [meetingId, resumeRevision])

  return meetingId
    ? <div ref={containerRef} className='h-full' id='videoSDKContainer' />
    : null
}

export default VideoSDK
